import React, { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Bell, X, Check, AlertTriangle, Info, AlertCircle, Zap } from 'lucide-react'
import { format } from 'date-fns'
import toast from 'react-hot-toast'
import { unifiedAdminApiClient as adminApi } from '../services/unified-admin-api-client'
import { useAdminNotificationUpdates } from '../services/websocket'
import { AdminNotificationResponse } from '../types'

interface NotificationDropdownProps {
  className?: string
}

export default function NotificationDropdown({ className = '' }: NotificationDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [page, setPage] = useState(1)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  // Get notifications
  const { data: notificationsData, isLoading, error, refetch } = useQuery(
    ['admin-notifications', page],
    () => adminApi.getNotifications({ page, per_page: 10 }),
    {
      retry: 3,
      retryDelay: 1000,
      refetchInterval: 30000, // Refetch every 30 seconds
      onError: (error: any) => {
        console.error('Failed to load notifications:', error)
      }
    }
  )

  // Get unread count for badge
  const { data: unreadCountData } = useQuery(
    'admin-notifications-unread-count',
    () => adminApi.getUnreadNotificationCount(),
    {
      retry: 3,
      retryDelay: 1000,
      refetchInterval: 10000, // Refetch every 10 seconds
    }
  )

  // Mark as read mutation
  const markAsReadMutation = useMutation(
    (notificationId: string) => adminApi.markNotificationAsRead(notificationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-notifications')
        queryClient.invalidateQueries('admin-notifications-unread-count')
      },
      onError: () => {
        toast.error('Failed to mark notification as read')
      },
    }
  )

  // Delete notification mutation
  const deleteNotificationMutation = useMutation(
    (notificationId: string) => adminApi.deleteNotification(notificationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-notifications')
        queryClient.invalidateQueries('admin-notifications-unread-count')
        toast.success('Notification deleted')
      },
      onError: () => {
        toast.error('Failed to delete notification')
      },
    }
  )

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation(
    () => adminApi.markAllNotificationsAsRead(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-notifications')
        queryClient.invalidateQueries('admin-notifications-unread-count')
        toast.success('All notifications marked as read')
      },
      onError: () => {
        toast.error('Failed to mark all notifications as read')
      },
    }
  )

  // WebSocket real-time updates
  useAdminNotificationUpdates((data) => {
    console.log('📢 Real-time admin notification received:', data)
    // Refetch notifications and unread count when new notification arrives
    queryClient.invalidateQueries('admin-notifications')
    queryClient.invalidateQueries('admin-notifications-unread-count')
    
    // Show toast notification for new notifications
    if (data.notification) {
      const notification = data.notification
      toast.custom((t) => (
        <div className={`${
          t.visible ? 'animate-enter' : 'animate-leave'
        } max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}>
          <div className="flex-1 w-0 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {getSeverityIcon(notification.severity)}
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {notification.title}
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  {notification.message}
                </p>
              </div>
            </div>
          </div>
          <div className="flex border-l border-gray-200">
            <button
              onClick={() => toast.dismiss(t.id)}
              className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              Close
            </button>
          </div>
        </div>
      ), {
        duration: 5000,
        position: 'top-right',
      })
    }
  })

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const notifications = notificationsData?.success ? notificationsData.data.data : []
  const unreadCount = unreadCountData?.success ? unreadCountData.data.unread_count : 0

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertCircle className="h-4 w-4 text-red-500" />
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-red-500" />
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />
      case 'info':
      default:
        return <Info className="h-4 w-4 text-blue-500" />
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'border-l-red-500 bg-red-50'
      case 'error':
        return 'border-l-red-500 bg-red-50'
      case 'warning':
        return 'border-l-yellow-500 bg-yellow-50'
      case 'info':
      default:
        return 'border-l-blue-500 bg-blue-50'
    }
  }

  const handleMarkAsRead = (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation()
    markAsReadMutation.mutate(notificationId)
  }

  const handleDelete = (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation()
    deleteNotificationMutation.mutate(notificationId)
  }

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate()
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Notification Bell Button */}
      <button
        className="text-gray-500 hover:text-gray-700 relative transition-colors"
        onClick={() => setIsOpen(!isOpen)}
        title="View notifications"
      >
        <Bell className="h-6 w-6" />
        {/* Notification Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 rounded-full flex items-center justify-center">
            <span className="text-xs text-white font-medium">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center space-x-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  disabled={markAllAsReadMutation.isLoading}
                  className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                >
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="px-4 py-8 text-center text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                Loading notifications...
              </div>
            ) : error ? (
              <div className="px-4 py-8 text-center text-red-500">
                <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
                Failed to load notifications
                <button
                  onClick={() => refetch()}
                  className="block mx-auto mt-2 text-sm text-blue-600 hover:text-blue-800"
                >
                  Try again
                </button>
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                No notifications
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification: AdminNotificationResponse) => (
                  <div
                    key={notification.id}
                    className={`px-4 py-3 hover:bg-gray-50 cursor-pointer border-l-4 ${getSeverityColor(notification.severity)} ${
                      !notification.is_read ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          {getSeverityIcon(notification.severity)}
                          <h4 className={`text-sm font-medium ${
                            !notification.is_read ? 'text-gray-900' : 'text-gray-700'
                          }`}>
                            {notification.title}
                          </h4>
                          {!notification.is_read && (
                            <span className="h-2 w-2 bg-blue-500 rounded-full"></span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          {notification.message}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">
                            {format(new Date(notification.created_at), 'MMM d, yyyy h:mm a')}
                          </span>
                          <span className="text-xs text-gray-400 capitalize">
                            {notification.notification_type.replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1 ml-2">
                        {!notification.is_read && (
                          <button
                            onClick={(e) => handleMarkAsRead(notification.id, e)}
                            disabled={markAsReadMutation.isLoading}
                            className="p-1 text-gray-400 hover:text-green-600 disabled:opacity-50"
                            title="Mark as read"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={(e) => handleDelete(notification.id, e)}
                          disabled={deleteNotificationMutation.isLoading}
                          className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-50"
                          title="Delete notification"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 text-center">
              <button
                onClick={() => {
                  setIsOpen(false)
                  // TODO: Navigate to full notifications page
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}